import type { CsvDelimiter, ImportValueFix } from '@ofmos/shared/csv';
import type {
  CreateExportInput,
  CsvClient,
  ExportRunView,
  ImportColumnReview,
  ImportConfigPatch,
  ImportPreviewView,
  ImportRunView,
  ImportUploadResult,
  ImportWizardState,
  RevertPreviewView,
} from '../client';
import type { Transport } from './transport';

/**
 * `CsvClient` over `/api/imports/*` and `/api/exports/*`
 * (`docs/decisions/csv-import-export.md` section 10).
 *
 * Thin, like the workflow and webhook clients, and here the thinness is not only a
 * convention: every rule this module has - which caps a file breaks, whether a mapping is
 * legal, whether the object is already held by another run, whether the requester still
 * holds `IMPORT_CSV` - is enforced on the server and re-checked when the worker picks the
 * run up (D23, D30, D33). A second opinion in the browser could only ever be a stale one,
 * and the screens surface the refusal as the sentence the server wrote.
 *
 * TWO ROUTES ARE NOT JSON, and both are deliberate rather than incidental. The upload PUTs
 * the file as a raw `text/csv` body, because D3 answers the 1 MB wall with a route-level
 * limit and a raw parser rather than multipart. The three file reads answer with CSV text,
 * which `requestText` fetches through the same cookie, the same error mapping and the same
 * 401 hook as everything else.
 */
export class HttpCsvClient implements CsvClient {
  constructor(private readonly transport: Transport) {}

  async createImport(objectMetadataId: string): Promise<ImportRunView> {
    const response = await this.transport.request<{ run: ImportRunView }>({
      method: 'POST',
      path: '/api/imports',
      body: { objectMetadataId },
    });
    return response.run;
  }

  async uploadImportFile(
    runId: string,
    file: { readonly filename: string; readonly text: string; readonly delimiter?: CsvDelimiter },
  ): Promise<ImportUploadResult> {
    return this.transport.request<ImportUploadResult>({
      method: 'PUT',
      path: `/api/imports/${runId}/file`,
      query: {
        filename: file.filename,
        // Absent means "sniff it" (D2). Sending the sniffed value back would turn the
        // wizard's own display of the guess into an override of it.
        ...(file.delimiter === undefined ? {} : { delimiter: file.delimiter }),
      },
      rawBody: { contentType: 'text/csv', body: file.text },
    });
  }

  async listImports(limit?: number): Promise<readonly ImportRunView[]> {
    const response = await this.transport.request<{ runs: readonly ImportRunView[] }>({
      method: 'GET',
      path: '/api/imports',
      query: { limit: limit ?? 50 },
    });
    return response.runs;
  }

  async getImport(runId: string): Promise<ImportRunView> {
    const response = await this.transport.request<{ run: ImportRunView }>({
      method: 'GET',
      path: `/api/imports/${runId}`,
    });
    return response.run;
  }

  async getImportWizard(runId: string): Promise<{
    readonly run: ImportRunView;
    readonly wizard: ImportWizardState;
  }> {
    return this.transport.request({
      method: 'GET',
      path: `/api/imports/${runId}`,
      query: { wizard: 'true' },
    });
  }

  async updateImport(runId: string, patch: ImportConfigPatch): Promise<ImportRunView> {
    const response = await this.transport.request<{ run: ImportRunView }>({
      method: 'PATCH',
      path: `/api/imports/${runId}`,
      body: patch,
    });
    return response.run;
  }

  async reviewColumn(runId: string, columnIndex: number): Promise<ImportColumnReview> {
    return this.transport.request({
      method: 'GET',
      path: `/api/imports/${runId}/columns/${columnIndex}/values`,
    });
  }

  async setValueFixes(
    runId: string,
    columnIndex: number,
    fixes: readonly ImportValueFix[],
  ): Promise<void> {
    await this.transport.request({
      method: 'POST',
      path: `/api/imports/${runId}/columns/${columnIndex}/value-fixes`,
      body: { fixes },
    });
  }

  async previewImport(runId: string): Promise<{
    readonly run: ImportRunView;
    readonly preview: ImportPreviewView;
  }> {
    return this.transport.request({
      method: 'POST',
      path: `/api/imports/${runId}/preview`,
      body: {},
    });
  }

  async startImport(runId: string): Promise<ImportRunView> {
    const response = await this.transport.request<{ run: ImportRunView }>({
      method: 'POST',
      path: `/api/imports/${runId}/run`,
      body: {},
    });
    return response.run;
  }

  async retryImport(runId: string): Promise<ImportRunView> {
    const response = await this.transport.request<{ run: ImportRunView }>({
      method: 'POST',
      path: `/api/imports/${runId}/retry`,
      body: {},
    });
    return response.run;
  }

  async cancelImport(runId: string): Promise<ImportRunView> {
    const response = await this.transport.request<{ run: ImportRunView }>({
      method: 'POST',
      path: `/api/imports/${runId}/cancel`,
      body: {},
    });
    return response.run;
  }

  async removeImport(runId: string): Promise<void> {
    await this.transport.requestMaybe({ method: 'DELETE', path: `/api/imports/${runId}` });
  }

  async revertPreview(runId: string): Promise<RevertPreviewView> {
    const response = await this.transport.request<{ revertPreview: RevertPreviewView }>({
      method: 'GET',
      path: `/api/imports/${runId}`,
      query: { revertPreview: 'true' },
    });
    return response.revertPreview;
  }

  async revertImport(runId: string): Promise<ImportRunView> {
    const response = await this.transport.request<{ run: ImportRunView }>({
      method: 'POST',
      path: `/api/imports/${runId}/revert`,
      body: {},
    });
    return response.run;
  }

  async importErrorsCsv(runId: string): Promise<string> {
    return this.transport.requestText({
      method: 'GET',
      path: `/api/imports/${runId}/errors.csv`,
    });
  }

  async revertReportCsv(runId: string): Promise<string> {
    return this.transport.requestText({
      method: 'GET',
      path: `/api/imports/${runId}/report.csv`,
    });
  }

  async createExport(input: CreateExportInput): Promise<ExportRunView> {
    const response = await this.transport.request<{ run: ExportRunView }>({
      method: 'POST',
      path: '/api/exports',
      body: {
        query: input.query,
        ...(input.recordIds === undefined ? {} : { recordIds: input.recordIds }),
        allFields: input.allFields ?? false,
      },
    });
    return response.run;
  }

  async listExports(limit?: number): Promise<readonly ExportRunView[]> {
    const response = await this.transport.request<{ runs: readonly ExportRunView[] }>({
      method: 'GET',
      path: '/api/exports',
      query: { limit: limit ?? 50 },
    });
    return response.runs;
  }

  async getExport(runId: string): Promise<ExportRunView> {
    const response = await this.transport.request<{ run: ExportRunView }>({
      method: 'GET',
      path: `/api/exports/${runId}`,
    });
    return response.run;
  }

  async retryExport(runId: string): Promise<ExportRunView> {
    const response = await this.transport.request<{ run: ExportRunView }>({
      method: 'POST',
      path: `/api/exports/${runId}/retry`,
      body: {},
    });
    return response.run;
  }

  async exportFileCsv(runId: string): Promise<string> {
    return this.transport.requestText({
      method: 'GET',
      path: `/api/exports/${runId}/file`,
    });
  }
}
